/**
 * Tenant Context
 *
 * Manages the current tenant selection for multi-tenant isolation.
 * Integrates with AuthContext for access control.
 * The selected tenant ID is passed to all API calls via the X-Tenant-ID header.
 */

import {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import { useAuth, getTenantById, type Tenant } from "./auth-context";

// Re-export Tenant type for convenience
export type { Tenant };

interface TenantContextType {
  currentTenant: Tenant;
  tenantId: string;
  setTenantId: (id: string) => void;
  isLoading: boolean;
  availableTenants: Tenant[];
}

const TenantContext = createContext<TenantContextType | null>(null);

const TENANT_STORAGE_KEY = "selectedTenantId";

// Default tenant for fallback
const DEFAULT_TENANT: Tenant = {
  id: "lululemon",
  name: "lululemon",
  slug: "lululemon",
  plan: "DEMO",
  color: "#C41230",
  initial: "L",
};

export function TenantProvider({ children }: { children: ReactNode }) {
  const { user, isAuthenticated, getAuthorizedTenants, canAccessTenant } =
    useAuth();
  const [tenantId, setTenantIdState] = useState<string>(() => {
    if (typeof window !== "undefined") {
      return localStorage.getItem(TENANT_STORAGE_KEY) || "";
    }
    return "";
  });
  const [isLoading, setIsLoading] = useState(true);

  // Get authorized tenants from auth context
  const availableTenants = getAuthorizedTenants();

  // Determine current tenant based on auth and selection
  const getCurrentTenant = (): Tenant => {
    // If not authenticated, return default
    if (!isAuthenticated || !user) {
      return DEFAULT_TENANT;
    }

    // If user has a selected tenant and can access it, use that
    if (tenantId && canAccessTenant(tenantId)) {
      const tenant = getTenantById(tenantId);
      if (tenant) return tenant;
    }

    // Otherwise use user's primary tenant
    const primaryTenant = getTenantById(user.tenantId);
    if (primaryTenant) return primaryTenant;

    // Fallback to first authorized tenant
    if (availableTenants.length > 0) {
      return availableTenants[0];
    }

    return DEFAULT_TENANT;
  };

  const currentTenant = getCurrentTenant();

  // Sync tenant selection when auth changes
  useEffect(() => {
    if (isAuthenticated && user) {
      // If current selection is not authorized, switch to user's primary tenant
      if (!canAccessTenant(tenantId)) {
        setTenantIdState(user.tenantId);
        localStorage.setItem(TENANT_STORAGE_KEY, user.tenantId);
      }
    }
    setIsLoading(false);
  }, [isAuthenticated, user, tenantId, canAccessTenant]);

  const setTenantId = (id: string) => {
    // Only allow switching to authorized tenants
    if (canAccessTenant(id)) {
      setTenantIdState(id);
      localStorage.setItem(TENANT_STORAGE_KEY, id);
    } else {
      console.warn(`User not authorized to access tenant: ${id}`);
    }
  };

  return (
    <TenantContext.Provider
      value={{
        currentTenant,
        tenantId: currentTenant.id,
        setTenantId,
        isLoading,
        availableTenants,
      }}
    >
      {children}
    </TenantContext.Provider>
  );
}

export function useTenant() {
  const context = useContext(TenantContext);
  if (!context) {
    throw new Error("useTenant must be used within a TenantProvider");
  }
  return context;
}

// Legacy export for backwards compatibility with DEMO_TENANTS references
export const DEMO_TENANTS = [
  {
    id: "lululemon",
    name: "Lululemon Demo",
    slug: "lululemon",
    plan: "DEMO",
    color: "#C41230",
    initial: "L",
  },
  {
    id: "arcteryx",
    name: "Arc'Teryx Demo",
    slug: "arcteryx",
    plan: "DEMO",
    color: "#000000",
    initial: "A",
  },
  {
    id: "nespresso",
    name: "Nespresso Demo",
    slug: "nespresso",
    plan: "DEMO",
    color: "#6B4423",
    initial: "N",
  },
  {
    id: "lego",
    name: "LEGO Demo",
    slug: "lego",
    plan: "DEMO",
    color: "#FFD500",
    initial: "L",
  },
  {
    id: "nike",
    name: "Nike Demo",
    slug: "nike",
    plan: "DEMO",
    color: "#111111",
    initial: "N",
  },
  {
    id: "adidas",
    name: "Adidas Demo",
    slug: "adidas",
    plan: "DEMO",
    color: "#000000",
    initial: "A",
  },
  {
    id: "demobrand",
    name: "Demo Brand",
    slug: "demobrand",
    plan: "DEMO",
    color: "#0066CC",
    initial: "D",
  },
  {
    id: "otherware",
    name: "Otherware",
    slug: "otherware",
    plan: "DEMO",
    color: "#2D2D2D",
    initial: "O",
  },
];
